import { WorkSpaceSettings } from "@/core/entities/workspace";
import { DateService, NumberFormatterService } from "@/core/services";
import { NumberFormatType } from "@/core/types";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { Division, Highlighter, Skeleton, TableColumnType, TableColumnVerticalAlign } from "@ir/ui";

export const fakeDeductionItemColumnsSkeleton = (width?: number) => {
  return <Skeleton height={9} width={width} animation="wave" variant="rounded" className="my-1" />;
};

export const fakeDeductionItems: any[] = [
  {
    id: 1,
  },
  {
    id: 2,
  },
  {
    id: 3,
  },
  {
    id: 4,
  },
];

export const fakeDeductionItemColumns: TableColumnType<any>[] = [
  {
    key: "id",
    title: <>&nbsp;</>,
    dataIndex: "id",
    cellRender: () => fakeDeductionItemColumnsSkeleton(),
  },
  {
    key: "id2",
    title: <>&nbsp;</>,
    dataIndex: "id",
    cellRender: () => fakeDeductionItemColumnsSkeleton(),
  },
  {
    key: "id3",
    title: <>&nbsp;</>,
    dataIndex: "id",
    cellRender: () => fakeDeductionItemColumnsSkeleton(),
  },
  {
    key: "id4",
    title: <>&nbsp;</>,
    dataIndex: "id",
    cellRender: () => fakeDeductionItemColumnsSkeleton(),
  },
];

export const verticalAlign: TableColumnVerticalAlign = "top";

export enum CellType {
  NUMBER = "number",
  TEXT = "text",
  DATE = "date",
}

export type ColumnProps = {
  width: number;
  title: string;
  dataIndex: string;
  key: string;
  verticalAlign?: TableColumnVerticalAlign;
  fixed?: "left" | "right";
  align?: "left" | "center" | "right";
  cellRender?: (value: unknown) => React.ReactNode;
  cellType?: CellType;
  hidden?: boolean;
};

const getWorkBreakCellRender = (value: string, keywords?: string[]) => {
  return (
    <Highlighter keywords={keywords ?? []}>
      <Division style={{ wordBreak: "break-word", textWrap: "wrap" }}>{value}</Division>
    </Highlighter>
  );
};

const getCellRenderDate = (value: string, format: string | null, keywords?: string[]) => {
  const keywordsToUse = keywords ?? [];
  if (!format) {
    return getWorkBreakCellRender(value, keywordsToUse);
  }
  const formattedValue = new DateService(new DateAdapter()).format(value, format);
  return getWorkBreakCellRender(formattedValue, keywordsToUse);
};

const getCellRenderNumber = (value: number, format: string | null, keywords?: string[]) => {
  const keywordsToUse = keywords ?? [];
  if (!format) {
    return getWorkBreakCellRender(value.toString(), keywordsToUse);
  }
  const formattedValue = new NumberFormatterService(new NumberFormatterAdapter()).format(value, format as NumberFormatType);
  return getWorkBreakCellRender(formattedValue, keywordsToUse);
};

export const getColumnProps = (keywords?: string[], workspaceSettings?: WorkSpaceSettings | null) => {
  const displayDateFormat = workspaceSettings?.displayDateFormat ?? null;
  const displayNumberFormat = workspaceSettings?.displayNumberFormat ?? null;

  const columnProps: ColumnProps[] = [
    {
      width: 180,
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      align: "left",
      cellRender: notesValue => {
        return getWorkBreakCellRender(notesValue as string, keywords);
      },
    },
    {
      width: 120,
      title: "PO Date",
      dataIndex: "poDate",
      key: "poDate",
      verticalAlign: verticalAlign,
      cellType: CellType.DATE,
      align: "right",
      cellRender: poDateValue => {
        return poDateValue ? getCellRenderDate(poDateValue as string, displayDateFormat, keywords) : null;
      },
    },
    {
      width: 80,
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      verticalAlign: verticalAlign,
      align: "right",
      cellRender: quantityValue => {
        return quantityValue ? getCellRenderNumber(quantityValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 100,
      title: "Allowance",
      dataIndex: "allowance",
      key: "allowance",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: allowanceValue => {
        return getWorkBreakCellRender(allowanceValue as string, keywords);
      },
    },
    {
      width: 100,
      title: "Pack Size",
      dataIndex: "packSize",
      key: "packSize",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: packSizeValue => {
        return getWorkBreakCellRender(packSizeValue as string, keywords);
      },
    },
    {
      width: 160,
      title: "PO Number",
      dataIndex: "poNumber",
      key: "poNumber",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      align: "left",
      cellRender: poNumberValue => {
        return <Division style={{ wordBreak: "break-word", textWrap: "wrap" }}>{getWorkBreakCellRender(poNumberValue as string, keywords)}</Division>;
      },
    },
    {
      width: 120,
      title: "Ship Date",
      dataIndex: "shipDate",
      key: "shipDate",
      verticalAlign: verticalAlign,
      cellType: CellType.DATE,
      align: "right",
      cellRender: shipDateValue => {
        return shipDateValue ? getCellRenderDate(shipDateValue as string, displayDateFormat, keywords) : null;
      },
    },
    {
      width: 100,
      title: "Unit Cost",
      dataIndex: "unitCost",
      key: "unitCost",
      verticalAlign: verticalAlign,
      cellType: CellType.NUMBER,
      align: "right",
      cellRender: unitCostValue => {
        return unitCostValue ? getCellRenderNumber(unitCostValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      verticalAlign: verticalAlign,
      cellType: CellType.DATE,
      align: "right",
      cellRender: expiryDateValue => {
        return expiryDateValue ? getCellRenderDate(expiryDateValue as string, displayDateFormat, keywords) : null;
      },
    },
    {
      width: 100,
      title: "Ref Invoice",
      dataIndex: "refInvoice",
      key: "refInvoice",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: refInvoiceValue => {
        return getWorkBreakCellRender(refInvoiceValue as string, keywords);
      },
    },
    {
      width: 120,
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      verticalAlign: verticalAlign,
      cellType: CellType.DATE,
      align: "right",
      cellRender: invoiceDateValue => {
        return invoiceDateValue ? getCellRenderDate(invoiceDateValue as string, displayDateFormat, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Deducted Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      verticalAlign: verticalAlign,
      cellType: CellType.NUMBER,
      align: "right",
      fixed: "left",
      cellRender: totalAmountValue => {
        return totalAmountValue ? getCellRenderNumber(totalAmountValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Received Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
      verticalAlign: verticalAlign,
      cellType: CellType.DATE,
      align: "right",
      cellRender: receivedDateValue => {
        return receivedDateValue ? getCellRenderDate(receivedDateValue as string, displayDateFormat, keywords) : null;
      },
    },
    {
      width: 220,
      title: "Retailer Name",
      dataIndex: "retailerName",
      key: "retailerName",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: retailerNameValue => {
        return <Division style={{ wordBreak: "break-word", textWrap: "wrap" }}>{getWorkBreakCellRender(retailerNameValue as string, keywords)}</Division>;
      },
    },
    {
      width: 120,
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      verticalAlign: verticalAlign,
      cellType: CellType.NUMBER,
      align: "right",
      cellRender: invoiceAmountValue => {
        return invoiceAmountValue ? getCellRenderNumber(invoiceAmountValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Order Quantity",
      dataIndex: "orderQuantity",
      key: "orderQuantity",
      verticalAlign: verticalAlign,
      align: "right",
      cellRender: orderQuantityValue => {
        return orderQuantityValue ? getCellRenderNumber(orderQuantityValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Cost Difference",
      dataIndex: "costDifference",
      key: "costDifference",
      verticalAlign: verticalAlign,
      cellType: CellType.NUMBER,
      align: "right",
      cellRender: costDifferenceValue => {
        return costDifferenceValue ? getCellRenderNumber(costDifferenceValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 150,
      title: "Retailer Number",
      dataIndex: "retailerNumber",
      key: "retailerNumber",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: retailerNumberValue => {
        return getWorkBreakCellRender(retailerNumberValue as string, keywords);
      },
    },
    {
      width: 150,
      title: "UPC Item Number",
      dataIndex: "upcItemNumber",
      key: "upcItemNumber",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      fixed: "left",
      cellRender: upcItemNumberValue => {
        return getWorkBreakCellRender(upcItemNumberValue as string, keywords);
      },
    },
    {
      width: 120,
      title: "Product Category",
      dataIndex: "productCategoryName",
      key: "productCategoryName",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: productCategoryNameValue => {
        return getWorkBreakCellRender(productCategoryNameValue as string, keywords);
      },
    },
    {
      width: 220,
      title: "Item Description",
      dataIndex: "itemDescription",
      key: "itemDescription",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: itemDescriptionValue => {
        return getWorkBreakCellRender(itemDescriptionValue as string, keywords);
      },
    },
    {
      width: 150,
      title: "Received Unit Cost",
      dataIndex: "receivedUnitCost",
      key: "receivedUnitCost",
      verticalAlign: verticalAlign,
      cellType: CellType.NUMBER,
      align: "right",
      cellRender: receivedUnitCostValue => {
        return receivedUnitCostValue ? getCellRenderNumber(receivedUnitCostValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 100,
      title: "Distribution Center",
      dataIndex: "distributionCenter",
      key: "distributionCenter",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: distributionCenterValue => {
        return getWorkBreakCellRender(distributionCenterValue as string, keywords);
      },
    },
    {
      width: 150,
      title: "Quantity Difference",
      dataIndex: "quantityDifference",
      key: "quantityDifference",
      verticalAlign: verticalAlign,
      align: "right",
      cellRender: quantityDifferenceValue => {
        return quantityDifferenceValue ? getCellRenderNumber(quantityDifferenceValue as number, displayNumberFormat, keywords) : null;
      },
    },
    {
      width: 100,
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      align: "right",
      cellRender: discountValue => {
        return discountValue ? getWorkBreakCellRender(discountValue as string, keywords) : null;
      },
    },
    {
      width: 120,
      title: "Division Name",
      dataIndex: "divisionName",
      key: "divisionName",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: divisionNameValue => {
        return getWorkBreakCellRender(divisionNameValue as string, keywords);
      },
    },
    {
      width: 120,
      title: "Division Number",
      dataIndex: "divisionNumber",
      key: "divisionNumber",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: divisionNumberValue => {
        return getWorkBreakCellRender(divisionNumberValue as string, keywords);
      },
    },
    {
      width: 100,
      title: "Units",
      dataIndex: "units",
      key: "units",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      align: "right",
    },
    {
      width: 100,
      title: "Ship",
      dataIndex: "ship",
      key: "ship",
      verticalAlign: verticalAlign,
      cellType: CellType.TEXT,
      cellRender: shipValue => {
        return getWorkBreakCellRender(shipValue as string, keywords);
      },
    },
  ];
  return columnProps;
};
